import { Box, Divider, MenuItem, Stack, Typography } from '@mui/material';
import { TextField, toast, useIsMobile } from '@side/pantry';
import { format } from 'date-fns';
import { useCallback, useMemo, useState } from 'react';
import { DataGrid } from '@/components/DataGrid';
import { ExportToCSVButton } from '../../components/ExportToCSVButton';
import { mapDataGridColumnToValueOrEmpty } from '../../helpers';
import {
  buildCsv,
  downloadCsv,
} from '../../ReportsExport/ReportsExport.helper';
import { TablePagination } from '../../TablePagination/TablePagination';
import type { GeographyGroupBy } from '../hooks/useGeographyPerformance';
import { useGeographyPerformance } from '../hooks/useGeographyPerformance';
import {
  DEFAULT_PAGE_SIZE,
  getGeographyPerformanceColumns,
  PAGE_SIZE_OPTIONS,
} from './GeographyPerformanceTable.helpers';

interface Props {
  viewReportAs: string;
}

export function GeographyPerformanceTable({ viewReportAs }: Props) {
  const isMobile = useIsMobile('md');
  const [groupedBy, setGroupedBy] = useState<GeographyGroupBy>('CITY');
  const { rows, isLoading } = useGeographyPerformance(viewReportAs, groupedBy);

  const [isExporting, setIsExporting] = useState(false);
  const columns = useMemo(
    () =>
      getGeographyPerformanceColumns(groupedBy).map((column) =>
        mapDataGridColumnToValueOrEmpty(column),
      ),
    [groupedBy],
  );

  const handleExportCsv = useCallback(async () => {
    if (rows.length === 0) return;

    setIsExporting(true);
    try {
      const csv = buildCsv(rows, columns);
      const filename = `Geography-${format(new Date(), 'yyyy-MM-dd')}.csv`;

      downloadCsv(csv, filename);
      toast('success', 'Geography report exported successfully', {
        showCloseButton: true,
      });
    } catch {
      toast(
        'error',
        'Export failed. Please try again or contact Side Support for assistance.',
        { showCloseButton: true },
      );
    } finally {
      setIsExporting(false);
    }
  }, [rows, columns]);

  const showPagination = rows.length > DEFAULT_PAGE_SIZE;

  return (
    <Stack sx={{ gap: 2 }} id="geography-performance-table">
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Stack
          sx={{
            alignItems: 'center',
            gap: 2,
            flexDirection: 'row',
          }}
        >
          <Typography variant={isMobile ? 'subtitle2' : 'subtitle1'}>
            Geography
          </Typography>
          <TextField
            select
            value={groupedBy}
            onChange={(e) => setGroupedBy(e.target.value as GeographyGroupBy)}
            size="small"
            sx={{ '& .MuiInputBase-root': { height: { xs: 31, md: 'auto' } } }}
            slotProps={{
              select: {
                renderValue: (value) => (
                  <Stack
                    sx={{ flexDirection: 'row', alignItems: 'center', gap: 1 }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      Group by
                    </Typography>
                    <Divider orientation="vertical" flexItem />
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {value === 'CITY' ? 'City' : 'ZIP Code'}
                    </Typography>
                  </Stack>
                ),
              },
            }}
          >
            <MenuItem value="CITY">City</MenuItem>
            <MenuItem value="ZIP_CODE">ZIP Code</MenuItem>
          </TextField>
        </Stack>
        <ExportToCSVButton
          onClick={handleExportCsv}
          loading={isExporting}
          disabled={rows.length === 0 || isLoading}
          sx={{ height: 'fit-content' }}
        />
      </Stack>
      <Box sx={{ position: 'relative' }}>
        <DataGrid
          key={groupedBy}
          variant="default"
          columns={columns}
          rows={rows}
          loading={isLoading}
          disableColumnFilter
          initialState={{
            pagination: {
              paginationModel: {
                page: 0,
                pageSize: DEFAULT_PAGE_SIZE,
              },
            },
            sorting: {
              sortModel: [{ field: 'salesVolume', sort: 'desc' }],
            },
          }}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          paginationMode="client"
          sortingMode="client"
          disableColumnMenu
          slots={{
            pagination: () => (
              <TablePagination
                variant="performance"
                hidePaginationControls={!showPagination}
              />
            ),
            noRowsOverlay: () => (
              <Typography
                variant="body3"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: 'text.secondary',
                }}
              >
                No performance data for selected filters
              </Typography>
            ),
          }}
          sx={{
            ...(rows.length === 0 && {
              '--DataGrid-overlayHeight': '60px',
            }),
            '& .MuiTablePagination-root': {
              justifyContent: 'flex-start',
              display: 'flex',
            },
            '& .MuiToolbar-root': {
              paddingLeft: 0,
            },
          }}
        />
      </Box>
    </Stack>
  );
}
